import { CONST } from '@common/constants/const.constant';
import { Button } from '@common/general-ui/button/button.component';
import { getInputLoqateLabel, IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { Input } from '@common/general-ui/input/input.component';
import { Modal } from '@common/general-ui/modal/modal.component';
import { t } from '@common/hooks/use-t.hook';
import { formatPostcode } from '@common/utils/format-postcode/format-postcode.util';
import React, { useCallback, useEffect, useState } from 'react';
import { css } from 'twin.macro';

interface IProps {
    name: string;
    value: IInputLoqateValue;
    onBlur?: (e: React.FocusEvent) => void;
    onOkClick?: (arg: { name: string; value: IInputLoqateValue }) => void;
    onCancelClick?: () => void;
    onCloseClick?: () => void;
}

export const ManualAddressModal = ({
    name,
    onOkClick,
    onCancelClick,
    onCloseClick,
    onBlur,
    ...rest
}: IProps): JSX.Element => {
    const [value, setValue] = useState<IInputLoqateValue>(rest.value);
    const [blur, setBlur] = useState<{ [key in keyof IInputLoqateValue]: boolean }>({
        addressLine1: false,
        addressLine2: false,
        addressLine3: false,
        city: false,
        country: false,
        label: false,
        originalLabel: false,
        postCode: false,
    });

    useEffect(() => {
        if (rest.value) {
            setValue({
                ...rest.value,
                label: getInputLoqateLabel(rest.value),
                originalLabel: getInputLoqateLabel(rest.value),
            });
        }
    }, [rest.value]);

    const createAddressChangeHandler = useCallback((propertyName: keyof IInputLoqateValue) => {
        return (e: React.ChangeEvent<HTMLInputElement>): void => {
            setValue((val: IInputLoqateValue | undefined): IInputLoqateValue => {
                const newValue = {
                    ...val,
                    [propertyName]: e.target.value,
                } as IInputLoqateValue;
                newValue.label = getInputLoqateLabel(newValue);
                newValue.originalLabel = getInputLoqateLabel(newValue);

                return newValue;
            });
        };
    }, []);

    const createBlurHandler = useCallback(
        (propertyName: keyof IInputLoqateValue) => (): void => {
            setBlur((prev) => {
                return {
                    ...prev,
                    [propertyName]: true,
                };
            });
        },
        [],
    );

    const handleOkClick = useCallback(() => {
        if (value.addressLine1 && value.city && value.postCode) {
            onOkClick && onOkClick({ name, value });
        }
    }, [name, value, onOkClick]);

    const createGetErrorMessage = (propertyName: keyof IInputLoqateValue, required: boolean): string | undefined => {
        const val = value ? value[propertyName] : undefined;
        if (!blur[propertyName]) {
            return;
        }
        if (required && !val) {
            return CONST.thisFieldRequired;
        }
        return undefined;
    };

    const handlePostcodeBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        setValue((val: IInputLoqateValue | undefined): IInputLoqateValue => {
            const postCode = formatPostcode(e.currentTarget.value);
            const newValue = {
                ...val,
                ['postCode']: postCode,
            } as IInputLoqateValue;
            newValue.label = getInputLoqateLabel(newValue);
            newValue.originalLabel = getInputLoqateLabel(newValue);

            return newValue;
        });
    };

    return (
        <Modal
            css={styles.root}
            name="enterAddressManually"
            title="Enter Address Manually"
            onCloseClick={onCloseClick}
            renderFooter={(): JSX.Element => (
                <div tw="flex sm:justify-end my-1">
                    <Button tw="w-full sm:w-min" name="cancel" variant="orange-outline" onClick={onCancelClick}>
                        Cancel
                    </Button>
                    <div tw="w-4"></div>
                    <Button tw="w-full sm:w-min" name="ok" variant="orange" onClick={handleOkClick}>
                        Check
                    </Button>
                </div>
            )}
        >
            <div tw="grid  grid-row-gap[16px] py-2">
                <Input
                    name="line1"
                    required
                    label={t('Address line 1')}
                    autoComplete="street-address"
                    type="text"
                    value={value?.addressLine1}
                    errorMessage={createGetErrorMessage('addressLine1', true)}
                    onBlur={createBlurHandler('addressLine1')}
                    onChange={createAddressChangeHandler('addressLine1')}
                />
                <div tw="grid sm:grid-cols-2 grid-column-gap[16px] grid-row-gap[16px]">
                    <Input
                        name="line2"
                        label={t('Address line 2')}
                        autoComplete="address-line2"
                        type="text"
                        value={value?.addressLine2}
                        errorMessage={createGetErrorMessage('addressLine2', false)}
                        onBlur={createBlurHandler('addressLine2')}
                        onChange={createAddressChangeHandler('addressLine2')}
                    />
                    <Input
                        name="line2"
                        label={t('Address line 3')}
                        autoComplete="address-line3"
                        type="text"
                        value={value?.addressLine3}
                        errorMessage={createGetErrorMessage('addressLine3', false)}
                        onBlur={createBlurHandler('addressLine3')}
                        onChange={createAddressChangeHandler('addressLine3')}
                    />
                </div>
                <div tw="grid sm:grid-cols-2 grid-column-gap[16px] grid-row-gap[16px] ">
                    <Input
                        name="city"
                        required
                        label={t('City')}
                        autoComplete="address-level2"
                        type="text"
                        value={value?.city}
                        errorMessage={createGetErrorMessage('city', true)}
                        onBlur={createBlurHandler('city')}
                        onChange={createAddressChangeHandler('city')}
                    />
                    <Input
                        name="postCode"
                        required
                        label={t('Postcode')}
                        autoComplete="postal-code"
                        type="text"
                        value={value?.postCode}
                        errorMessage={createGetErrorMessage('postCode', true)}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>): void => {
                            handlePostcodeBlur(e);
                            createBlurHandler('postCode')();
                        }}
                        onChange={createAddressChangeHandler('postCode')}
                    />
                </div>
            </div>
        </Modal>
    );
};

const styles = {
    root: css`
        max-width: 600px;
    `,
};

export const validateBillingAddress = (val: IInputLoqateValue): string | undefined => {
    return !val.addressLine1 || !val.city || !val.postCode || !val.country ? CONST.thisFieldRequired : undefined;
};
export const validateShippingAddress = (val: IInputLoqateValue): string | undefined => {
    return !val.addressLine1 ? CONST.thisFieldRequired : undefined;
};
